import { memo, useMemo, useRef, type ChangeEvent, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'styled-bootstrap-grid';

import type { AttachmentDto, PromotionMailTemplateDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { PromotionEmailPlaceholdersEnumValues } from '@/services/ws-funnel/src/main/resources/rest-api';
import { Field } from '@/js/libs/final-form';
import { generateRandomString } from '@/js/libs/framework/random';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useFieldState } from '@/js/libs/hooks/useFieldState';
import { Button, ButtonGroup, Label, PageHeader, FormGroup, InnerGroup } from '@/js/libs/ui';
import type { CustomToolbarItem } from '@/js/libs/ui/src/V2';
import { Container, Icons } from '@/js/libs/ui/src/V2';

import { PlaceholdersDropdown } from 'src/components/form/fields/RteField/components/PlaceholdersDropdown/index.tsx';
import RteField from 'src/components/form/fields/RteField/index.tsx';
import TextField from 'src/components/form/fields/TextField/index.tsx';
import { useInsertPlaceholder } from 'src/hooks/useInsertPlaceholder.ts';
import Status from 'src/scenes/Status/index.ts';
import type { LanguageKey } from 'src/types.ts';
import { PromotionLink } from '../../components/index.ts';
import { usePromotionContext } from '../../promotionContext.tsx';
import { Attachments } from './Attachments.tsx';
import { LanguageKeyToLanguageCodeMap } from './constants.ts';
import type { PromotionMailTemplateFormValues } from './form.ts';
import { LanguageTabs } from './LanguageTabs.tsx';
import { HiddenInput } from './styled.ts';
import type { UploadFile } from './types.ts';

type MailTemplateFormProps = {
  template?: PromotionMailTemplateDto;
  onSave: () => void;
  isSaving: boolean;
  onRemove?: () => void;
  templateAttachments: AttachmentDto[];
  setTemplateAttachments: Dispatch<SetStateAction<AttachmentDto[]>>;
  filesToUpload: UploadFile[];
  setFilesToUpload: Dispatch<SetStateAction<UploadFile[]>>;
  uploading: boolean;
  selectedLanguage: LanguageKey;
  setSelectedLanguage: Dispatch<SetStateAction<LanguageKey>>;
};

export const MailTemplateForm = memo(function MailTemplateForm({
  onSave,
  isSaving,
  onRemove,
  template,
  templateAttachments,
  setTemplateAttachments,
  filesToUpload,
  setFilesToUpload,
  uploading,
  selectedLanguage,
  setSelectedLanguage,
}: MailTemplateFormProps) {
  const [t] = useTranslation(['backend', 'common']);
  const { promotion } = usePromotionContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const subjectFieldName = `${selectedLanguage}.subject`;
  const messageFieldName = `${selectedLanguage}.body`;

  const [, setSubject] = useFieldState<Record<string, string>, typeof subjectFieldName>(subjectFieldName);

  const subjectRef = useRef<HTMLInputElement>(null);
  const insertPlaceholder = useInsertPlaceholder(subjectRef, setSubject);

  const selectedLanguageAttachments = useMemo(
    () => templateAttachments.filter((attachment) => attachment.language === LanguageKeyToLanguageCodeMap[selectedLanguage]),
    [selectedLanguage, templateAttachments],
  );

  const selectedLanguageFilesToUpload = useMemo(
    () => filesToUpload.filter((file) => file.language === LanguageKeyToLanguageCodeMap[selectedLanguage]),
    [filesToUpload, selectedLanguage],
  );

  const additionalToolbarItems = useMemo<CustomToolbarItem[]>(
    () => [
      {
        Icon: Icons.Paperclip,
        onClick: () => {
          inputRef.current?.click();
        },
      },
    ],
    [],
  );

  const handleFileSelect = useCallbackRef(async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget?.files ?? []);
    setFilesToUpload((prevFiles) => [
      ...prevFiles,
      ...files.map((file) => ({
        id: generateRandomString(),
        language: LanguageKeyToLanguageCodeMap[selectedLanguage],
        file,
      })),
    ]);
  });

  if (!promotion) {
    return <Status.NotFound />;
  }

  return (
    <>
      <PageHeader
        title={
          <div data-testid='promotion-link'>
            {t('serviceDashboard:EMAIL_TEMPLATE_FOR')}
            <PromotionLink.Promotion promotionId={promotion.id}>{` ${promotion.name}`}</PromotionLink.Promotion>
          </div>
        }
        actions={
          <ButtonGroup>
            {template?.id && (
              <Button variant='delete' onClick={onRemove} data-testid='delete-button'>
                {t('common:DELETE')}
              </Button>
            )}
            <Button onClick={onSave} isLoading={isSaving} data-testid='create-update-button'>
              {template?.id ? t('common:UPDATE') : t('common:CREATE')}
            </Button>
          </ButtonGroup>
        }
      />
      <Container dataTestId='content-main-wrapper'>
        <FormGroup>
          <Col md={12} data-testid='language-tabs-field'>
            <LanguageTabs value={selectedLanguage} onChange={setSelectedLanguage} />
          </Col>
        </FormGroup>
        <FormGroup>
          <Col md={12} data-testid='subject-field'>
            <Label name={subjectFieldName}>
              {t('common:SUBJECT')}
              <PlaceholdersDropdown placeholders={PromotionEmailPlaceholdersEnumValues} onPlaceholderClick={insertPlaceholder} />
            </Label>
            <Field<PromotionMailTemplateFormValues['en']['subject']>
              type='text'
              key={subjectFieldName}
              name={subjectFieldName}
              render={(props) => <TextField {...props} inputRef={subjectRef} />}
            />
          </Col>
        </FormGroup>
        <Row>
          <Col md={12}>
            <Row>
              <Col md={12} data-testid='message-field'>
                <InnerGroup>
                  <Label name={messageFieldName}>{t('common:MESSAGE')}</Label>
                  <Field<PromotionMailTemplateFormValues['en']['body']>
                    name={messageFieldName}
                    key={messageFieldName}
                    render={(props) => (
                      <RteField
                        withPreviewToggle
                        placeholders={PromotionEmailPlaceholdersEnumValues}
                        additionalToolbarItems={additionalToolbarItems}
                        {...props}
                      />
                    )}
                  />
                </InnerGroup>
                <HiddenInput type='file' onChange={handleFileSelect} ref={inputRef} multiple />
                <Attachments
                  templateAttachments={selectedLanguageAttachments}
                  setTemplateAttachments={setTemplateAttachments}
                  filesToUpload={selectedLanguageFilesToUpload}
                  setFilesToUpload={setFilesToUpload}
                  uploading={uploading}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
});
